import { useState } from "react";
import { Pencil } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ItemSearch from "@/components/purchase/ItemSearch";
import * as inventoryApi from "@/lib/inventoryApi";
import { BillPhotos, FlagList, money, num } from "@/components/approvals/shared";
import { errorText } from "@/lib/approvalsApi";

const PAY_MODES = [
  ["credit", "On credit (payable)"],
  ["cash_drawer", "Paid from the drawer"],
  ["upi", "Paid by UPI"],
  ["card", "Paid by card"],
  ["bank", "Paid by bank transfer"],
];

const clone = value => JSON.parse(JSON.stringify(value));
const round2 = n => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

/** The sum the server re-checks on approval: lines + tax + round-off + other charges - discount. */
export function billArithmetic(bill) {
  const lines = (bill.lines || []).reduce((sum, ln) => sum + Number(ln.amount_on_bill || 0), 0);
  const computed = lines + Number(bill.tax_amount || 0) + Number(bill.round_off || 0) + Number(bill.other_charges || 0) - Number(bill.discount || 0);
  return { lines, computed, difference: round2(Number(bill.total || 0) - computed) };
}

const LINE = "grid grid-cols-6 items-center gap-x-2 gap-y-2 p-3 md:grid-cols-[minmax(0,2fr)_minmax(0,2fr)_5rem_3rem_6rem_4rem_6rem] md:p-2";

/** A labelled cell: the label shows on a phone (where the column header is gone) and is hidden from md up. */
function LineCell({ label, className = "", children }) {
  return (
    <div className={`min-w-0 ${className}`}>
      <span className="mb-0.5 block text-micro text-muted-foreground md:sr-only">{label}</span>
      {children}
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`flex flex-col gap-1 ${className}`}>
      <span className="text-micro font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

export default function BillReview({ actionId, type, draft, setDraft, context, token, readOnly }) {
  const isBundle = type === "bill_bundle";
  const bill = isBundle ? draft.bill : draft;
  const supplier = isBundle ? draft.supplier : null;
  const newItems = isBundle ? draft.new_items || [] : [];
  const [picked, setPicked] = useState({});        // line index -> name of an item chosen here
  const [searching, setSearching] = useState(null); // line index whose search box is open
  const [catalogue, setCatalogue] = useState(null);
  const [catalogueError, setCatalogueError] = useState("");

  const edit = change => {
    const next = clone(draft);
    change(isBundle ? next.bill : next, next);
    setDraft(next);
  };
  const setBill = (key, value) => edit(b => { b[key] = value; });
  const setLine = (i, key, value) => edit(b => {
    const ln = b.lines[i];
    ln[key] = value;
    if (key === "qty" || key === "rate") {
      ln.amount_on_bill = round2(Number(ln.qty || 0) * Number(ln.rate || 0));
    }
  });
  const setSupplier = (key, value) => { const next = clone(draft); next.supplier[key] = value; setDraft(next); };
  const setNewItem = (i, key, value) => { const next = clone(draft); next.new_items[i][key] = value; setDraft(next); };

  const openSearch = async i => {
    setSearching(i);
    if (catalogue || catalogueError) return;
    try {
      setCatalogue(await inventoryApi.listItems({ activeOnly: true }, token));
    } catch (failure) {
      setCatalogueError(errorText(failure));
    }
  };
  const mapLine = (i, item) => {
    edit(b => { b.lines[i].stock_item_id = item.id; b.lines[i].pending_item_ref = null; });
    setPicked(p => ({ ...p, [i]: item.name }));
    setSearching(null);
  };

  const mappedName = (ln, i) => {
    if (picked[i]) return { text: picked[i], isNew: false };
    if (ln.stock_item_id) return { text: context?.item_names?.[String(ln.stock_item_id)]?.name || `Item #${ln.stock_item_id}`, isNew: false };
    const fresh = newItems.find(n => n.ref === ln.pending_item_ref);
    return { text: fresh?.name || ln.pending_item_ref || "Not matched", isNew: true };
  };

  const math = billArithmetic(bill);
  const balanced = Math.abs(math.difference) <= 1;
  const paymentMode = bill.payment?.mode || "credit";
  const disabled = Boolean(readOnly);
  const supplierName = isBundle
    ? (supplier?.supplier_id ? context?.supplier_name || `Supplier #${supplier.supplier_id}` : null)
    : context?.supplier_name || bill.supplier_name;

  return (
    <div className="grid gap-4 lg:grid-cols-2 min-h-0" data-testid="bill-review">
      <section className="min-h-0" aria-label="Bill photo">
        <BillPhotos actionId={actionId} attachmentIds={bill.attachment_ids || []} token={token} />
      </section>

      <section className="space-y-4 min-w-0" aria-label="Bill details">
        <FlagList flags={bill.flags} />

        <div className="rounded-md border p-3 space-y-3" data-testid="bill-supplier">
          <h3 className="text-body font-semibold">Supplier</h3>
          {supplierName ? (
            <p className="text-body" data-testid="supplier-existing">{supplierName} <Badge variant="outline">existing</Badge></p>
          ) : isBundle ? (
            <div className="grid gap-2 sm:grid-cols-2">
              <Badge variant="secondary" className="w-fit sm:col-span-2">New supplier — created when you approve</Badge>
              <Field label="Name"><Input className="h-9" disabled={disabled} value={supplier.name || ""} onChange={e => setSupplier("name", e.target.value)} data-testid="supplier-name" /></Field>
              <Field label="Phone"><Input className="h-9" disabled={disabled} value={supplier.phone || ""} onChange={e => setSupplier("phone", e.target.value)} /></Field>
              <Field label="GSTIN"><Input className="h-9" disabled={disabled} value={supplier.gstin || ""} onChange={e => setSupplier("gstin", e.target.value)} /></Field>
              <Field label="Payment terms (days)"><Input className="h-9" type="number" disabled={disabled} value={num(supplier.payment_terms_days ?? 0)} onChange={e => setSupplier("payment_terms_days", num(e.target.value))} /></Field>
            </div>
          ) : (
            <p className="text-body">{bill.supplier_name || "—"}</p>
          )}
        </div>

        <div className="rounded-md border p-3" data-testid="bill-header">
          <h3 className="text-body font-semibold mb-2">Bill</h3>
          <div className="grid gap-2 sm:grid-cols-2">
            <Field label="Invoice number"><Input className="h-9" disabled={disabled} value={bill.invoice_number || ""} onChange={e => setBill("invoice_number", e.target.value)} data-testid="bill-invoice-number" /></Field>
            <Field label="Invoice date"><Input className="h-9" type="date" disabled={disabled} value={bill.invoice_date || ""} onChange={e => setBill("invoice_date", e.target.value)} /></Field>
            <Field label="Payment">
              <Select disabled={disabled} value={paymentMode} onValueChange={value => setBill("payment", { ...(bill.payment || {}), mode: value })}>
                <SelectTrigger className="h-9" data-testid="bill-payment-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAY_MODES.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
        </div>

        <div className="rounded-md border" data-testid="bill-lines">
          <h3 className="text-body font-semibold p-3 pb-1">Lines</h3>
          {/* Grid rows, not a table: on a phone each line stacks (name, item, then the numbers) instead of scrolling sideways. */}
          <div className={`${LINE} hidden border-b bg-muted/40 text-micro font-medium text-muted-foreground md:grid`}>
            <span>As written on the bill</span><span>Mapped to</span><span>Qty</span><span>Unit</span><span>Rate</span><span>Tax %</span><span className="text-right">Amount</span>
          </div>
          {(bill.lines || []).map((ln, i) => {
            const mapped = mappedName(ln, i);
            return (
              <div key={i} className={`${LINE} border-b last:border-b-0`} data-testid={`bill-line-${i}`}>
                <LineCell label="As written on the bill" className="col-span-6 md:col-span-1">
                  <Input className="h-9 min-w-0" disabled={disabled} value={ln.description_on_bill || ""} onChange={e => setLine(i, "description_on_bill", e.target.value)} data-testid={`line-${i}-raw`} />
                </LineCell>
                <LineCell label="Mapped to" className="col-span-6 md:col-span-1">
                  <div className="flex items-center gap-1">
                    <span className="text-body" data-testid={`line-${i}-mapped`}>{mapped.text}</span>
                    {mapped.isNew && <Badge variant="secondary">new</Badge>}
                    {!disabled && <Button size="icon" variant="ghost" aria-label={`Change item for line ${i + 1}`} onClick={() => openSearch(i)}><Pencil /></Button>}
                  </div>
                  {searching === i && (
                    <div className="mt-1">
                      {catalogueError ? <p className="text-micro text-danger">{catalogueError}</p>
                        : <ItemSearch items={catalogue || []} onPick={item => mapLine(i, item)} placeholder="Search the right item…" testId={`line-${i}-search`} />}
                    </div>
                  )}
                </LineCell>
                <LineCell label="Qty" className="col-span-2 md:col-span-1"><Input className="h-9 min-w-0" type="number" step="any" disabled={disabled} value={num(ln.qty)} onChange={e => setLine(i, "qty", num(e.target.value))} data-testid={`line-${i}-qty`} /></LineCell>
                <LineCell label="Unit" className="col-span-2 order-last md:order-none md:col-span-1"><span className="text-body">{ln.unit}</span></LineCell>
                <LineCell label="Rate" className="col-span-2 md:col-span-1"><Input className="h-9 min-w-0" type="number" step="any" disabled={disabled} value={num(ln.rate)} onChange={e => setLine(i, "rate", num(e.target.value))} data-testid={`line-${i}-rate`} /></LineCell>
                <LineCell label="Tax %" className="col-span-2 md:col-span-1"><Input className="h-9 min-w-0" type="number" step="any" disabled={disabled} value={num(ln.tax_rate)} onChange={e => setLine(i, "tax_rate", num(e.target.value))} /></LineCell>
                <LineCell label="Amount" className="col-span-4 order-last text-right md:order-none md:col-span-1"><span className="tabular-nums" data-testid={`line-${i}-amount`}>{money(ln.amount_on_bill)}</span></LineCell>
              </div>
            );
          })}
        </div>

        {isBundle && newItems.length > 0 && (
          <div className="rounded-md border" data-testid="bill-new-items">
            <h3 className="text-body font-semibold p-3 pb-1">New stock items created with this bill</h3>
            {newItems.map((it, i) => (
              <div key={it.ref || i} className="grid grid-cols-1 gap-2 border-b p-3 last:border-b-0 sm:grid-cols-[minmax(0,2fr)_4rem_minmax(0,1fr)_minmax(0,2fr)] sm:items-center sm:p-2" data-testid={`new-item-${i}`}>
                <LineCell label="Name"><Input className="h-9 min-w-0" disabled={disabled} value={it.name || ""} onChange={e => setNewItem(i, "name", e.target.value)} data-testid={`new-item-${i}-name`} /></LineCell>
                <LineCell label="Unit"><span className="text-body">{it.unit}</span></LineCell>
                <LineCell label="Category"><Input className="h-9 min-w-0" disabled={disabled} value={it.category || ""} onChange={e => setNewItem(i, "category", e.target.value)} /></LineCell>
                <LineCell label="Other names"><Input className="h-9 min-w-0" disabled={disabled} value={(it.aliases || []).join(", ")} onChange={e => setNewItem(i, "aliases", e.target.value.split(",").map(a => a.trim()).filter(Boolean))} /></LineCell>
              </div>
            ))}
          </div>
        )}

        <div className="rounded-md border p-3 space-y-2" data-testid="bill-totals">
          <h3 className="text-body font-semibold">Totals</h3>
          <div className="grid gap-2 sm:grid-cols-3">
            <Field label="Tax"><Input className="h-9" type="number" step="any" disabled={disabled} value={num(bill.tax_amount)} onChange={e => setBill("tax_amount", num(e.target.value))} /></Field>
            <Field label="Round-off"><Input className="h-9" type="number" step="any" disabled={disabled} value={num(bill.round_off)} onChange={e => setBill("round_off", num(e.target.value))} /></Field>
            <Field label="Other charges"><Input className="h-9" type="number" step="any" disabled={disabled} value={num(bill.other_charges)} onChange={e => setBill("other_charges", num(e.target.value))} /></Field>
            <Field label="Discount"><Input className="h-9" type="number" step="any" disabled={disabled} value={num(bill.discount)} onChange={e => setBill("discount", num(e.target.value))} /></Field>
            <Field label="Grand total on the bill" className="sm:col-span-2"><Input className="h-9" type="number" step="any" disabled={disabled} value={num(bill.total)} onChange={e => setBill("total", num(e.target.value))} data-testid="bill-total" /></Field>
          </div>
          <p className={`text-meta rounded-md px-3 py-2 ${balanced ? "bg-success-subtle text-success" : "bg-danger-subtle text-danger"}`} data-testid="bill-balance">
            Lines ₹{money(math.lines)} + tax + round-off + other charges − discount = ₹{money(math.computed)}
            {balanced ? " — matches the total" : ` — ₹${money(Math.abs(math.difference))} ${math.difference > 0 ? "short of" : "over"} the total. Fix a line or the total before approving.`}
          </p>
        </div>
      </section>
    </div>
  );
}
